import React, { useEffect, useRef } from 'react';
import katex from 'katex';
import 'katex/dist/katex.min.css';

/**
 * Simple KaTeX-only renderer for questions
 * Handles LaTeX with delimiters and LaTeX-only text
 */
const KatexRenderer = ({ content, className = '' }) => {
  const containerRef = useRef(null);

  // Check if text looks like LaTeX
  const looksLikeLatex = (text) => {
    if (!text || typeof text !== 'string') return false;
    const latexPatterns = [
      /\\[a-zA-Z]+\{/,  // \command{
      /\\frac\{/,       // \frac{
      /\\sqrt/,         // \sqrt
      /\\sum/,          // \sum
      /\\int/,          // \int
      /\^\{/,           // ^{
      /_\{/,            // _{
      /\\begin\{/,      // \begin{
      /\\end\{/         // \end{
    ];
    return latexPatterns.some(pattern => pattern.test(text));
  };

  useEffect(() => {
    if (!containerRef.current || !content) return;

    const safeContent = typeof content === 'string' ? content : String(content || '');
    if (!safeContent) return;

    const container = containerRef.current;

    try {
      // Check if content is HTML
      const hasHtmlTags = safeContent.includes('<img') || 
                          safeContent.includes('<p>') || 
                          safeContent.includes('</p>') ||
                          safeContent.includes('<div>') ||
                          safeContent.includes('</div>') ||
                          safeContent.includes('<br') ||
                          safeContent.includes('<span') ||
                          safeContent.includes('</span>');

      if (hasHtmlTags) {
        // Handle HTML content
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = safeContent;

        // Process all text nodes
        const walker = document.createTreeWalker(
          tempDiv,
          NodeFilter.SHOW_TEXT,
          null
        );

        const textNodes = [];
        let node;
        while (node = walker.nextNode()) {
          const text = node.textContent;
          if (text && text.trim()) {
            textNodes.push({ node, text });
          }
        }

        // Process each text node
        textNodes.forEach(({ node: textNode, text }) => {
          const trimmedText = text.trim();
          
          // Check if entire text is LaTeX without delimiters
          if (looksLikeLatex(trimmedText) && 
              !trimmedText.startsWith('$') && 
              !trimmedText.startsWith('\\(') && 
              !trimmedText.startsWith('\\[') &&
              !trimmedText.includes('$') &&
              !trimmedText.includes('\\(') &&
              !trimmedText.includes('\\[')) {
            // Render as display math
            try {
              const mathElement = document.createElement('div');
              mathElement.style.textAlign = 'center';
              mathElement.style.margin = '10px 0';
              mathElement.className = 'katex-display';
              mathElement.innerHTML = katex.renderToString(trimmedText, { 
                throwOnError: false, 
                displayMode: true,
                strict: false
              });
              if (textNode && textNode.parentNode) {
                textNode.parentNode.replaceChild(mathElement, textNode);
              }
              return;
            } catch (e) {
              // If KaTeX fails, leave as is
            }
          }

          // Process delimited math
          const mathRegex = /(\$\$[^$]*?\$\$|\$[^$]*?\$|\\([^)]*?\\)|\\[[^\]]*?\\])/g;
          const matches = [...text.matchAll(mathRegex)];
          
          if (matches.length === 0) return;

          const parts = [];
          let lastIndex = 0;

          matches.forEach((match) => {
            const matchStart = match.index;
            let matchText = match[0];
            
            // Decode HTML entities
            matchText = matchText.replace(/&amp;/g, '&')
                               .replace(/&lt;/g, '<')
                               .replace(/&gt;/g, '>');
            
            if (matchStart > lastIndex) {
              parts.push({ type: 'text', content: text.substring(lastIndex, matchStart) });
            }
            
            let math = '';
            let isDisplay = false;
            
            if (matchText.startsWith('$$') && matchText.endsWith('$$')) {
              math = matchText.slice(2, -2);
              isDisplay = true;
            } else if (matchText.startsWith('$') && matchText.endsWith('$')) {
              math = matchText.slice(1, -1);
              isDisplay = false;
            } else if (matchText.startsWith('\\[') && matchText.endsWith('\\]')) {
              math = matchText.slice(2, -2);
              isDisplay = true;
            } else if (matchText.startsWith('\\(') && matchText.endsWith('\\)')) {
              math = matchText.slice(2, -2);
              isDisplay = false;
            }
            
            if (math) {
              math = math.replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>');
              parts.push({ type: 'math', content: math, display: isDisplay });
            } else {
              parts.push({ type: 'text', content: matchText });
            }
            
            lastIndex = matchStart + match[0].length;
          });
          
          if (lastIndex < text.length) {
            parts.push({ type: 'text', content: text.substring(lastIndex) });
          }

          if (parts.some(p => p.type === 'math')) {
            const fragment = document.createDocumentFragment();
            
            parts.forEach((part) => {
              if (part.type === 'math') {
                const mathElement = part.display ? document.createElement('div') : document.createElement('span');
                if (part.display) {
                  mathElement.style.textAlign = 'center';
                  mathElement.style.margin = '10px 0';
                }
                mathElement.className = part.display ? 'katex-display' : 'katex-inline';
                
                try {
                  mathElement.innerHTML = katex.renderToString(part.content, { 
                    throwOnError: false, 
                    displayMode: part.display,
                    strict: false
                  });
                } catch (e) {
                  mathElement.textContent = part.content || '';
                }
                
                fragment.appendChild(mathElement);
              } else {
                fragment.appendChild(document.createTextNode(part.content || ''));
              }
            });
            
            if (textNode && textNode.parentNode) {
              textNode.parentNode.replaceChild(fragment, textNode);
            }
          }
        });

        container.innerHTML = tempDiv.innerHTML;
      } else {
        // Handle plain text content
        const trimmedText = safeContent.trim();
        
        // Check if entire text is LaTeX without delimiters
        if (looksLikeLatex(trimmedText) && 
            !trimmedText.startsWith('$') && 
            !trimmedText.startsWith('\\(') && 
            !trimmedText.startsWith('\\[') &&
            !trimmedText.includes('$') &&
            !trimmedText.includes('\\(') &&
            !trimmedText.includes('\\[')) {
          // Render as display math
          try {
            container.innerHTML = katex.renderToString(trimmedText, { 
              throwOnError: false, 
              displayMode: true,
              strict: false
            });
            container.style.textAlign = 'center';
            container.style.margin = '10px 0';
            return;
          } catch (e) {
            container.textContent = trimmedText;
            return;
          }
        }

        // Process delimited math in plain text
        const mathRegex = /(\$\$[^$]*?\$\$|\$[^$]*?\$|\\([^)]*?\\)|\\[[^\]]*?\\])/g;
        const matches = [...safeContent.matchAll(mathRegex)];
        
        if (matches.length === 0) {
          container.textContent = safeContent;
          return;
        }

        const parts = [];
        let lastIndex = 0;

        matches.forEach((match) => {
          const matchStart = match.index;
          const matchText = match[0];
          
          if (matchStart > lastIndex) {
            parts.push({ type: 'text', content: safeContent.substring(lastIndex, matchStart) });
          }
          
          let math = '';
          let isDisplay = false;
          
          if (matchText.startsWith('$$') && matchText.endsWith('$$')) {
            math = matchText.slice(2, -2);
            isDisplay = true;
          } else if (matchText.startsWith('$') && matchText.endsWith('$')) {
            math = matchText.slice(1, -1);
            isDisplay = false;
          } else if (matchText.startsWith('\\[') && matchText.endsWith('\\]')) {
            math = matchText.slice(2, -2);
            isDisplay = true;
          } else if (matchText.startsWith('\\(') && matchText.endsWith('\\)')) {
            math = matchText.slice(2, -2);
            isDisplay = false;
          }
          
          if (math) {
            parts.push({ type: 'math', content: math, display: isDisplay });
          } else {
            parts.push({ type: 'text', content: matchText });
          }
          
          lastIndex = matchStart + match[0].length;
        });
        
        if (lastIndex < safeContent.length) {
          parts.push({ type: 'text', content: safeContent.substring(lastIndex) });
        }

        // Render parts
        container.innerHTML = '';
        parts.forEach((part) => {
          if (part.type === 'math') {
            const mathElement = document.createElement(part.display ? 'div' : 'span');
            if (part.display) {
              mathElement.style.textAlign = 'center';
              mathElement.style.margin = '10px 0';
            }
            mathElement.className = part.display ? 'katex-display' : 'katex-inline';
            
            try {
              mathElement.innerHTML = katex.renderToString(part.content, { 
                throwOnError: false, 
                displayMode: part.display,
                strict: false
              });
            } catch (e) {
              mathElement.textContent = part.content || '';
            }
            
            container.appendChild(mathElement);
          } else {
            container.appendChild(document.createTextNode(part.content || ''));
          }
        });
      }
    } catch (error) {
      container.textContent = safeContent;
    }
  }, [content]);

  if (!content) return null;

  return (
    <div 
      ref={containerRef}
      className={className}
      style={{ whiteSpace: 'pre-wrap' }}
    />
  );
};

export default KatexRenderer;

